import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  query,
  orderBy,
  getDocs,
  startAfter,
  limit,
} from "firebase/firestore";
import { db } from "../firebase-config";
import { Link } from "react-router-dom";
import "../styles/postList.css";
import { formatDistanceToNow } from "date-fns";

const CACHE_KEY = "cachedPosts";

const PostList = () => {
  const [posts, setPosts] = useState([]); // Store posts in state
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true); // Track if more posts are available
  const lastVisibleRef = useRef(null); // Store the last fetched document for pagination

  // Fetch posts from Firestore with pagination
  const fetchPosts = async (isInitialLoad = false) => {
    if (loading) return; // Prevent duplicate fetches
    setLoading(true);

    try {
      let postsQuery;
      if (isInitialLoad || !lastVisibleRef.current) {
        postsQuery = query(
          collection(db, "posts"),
          orderBy("createdAt", "desc"),
          limit(10) // Limit to 10 posts instead of 20
        );
      } else {
        postsQuery = query(
          collection(db, "posts"),
          orderBy("createdAt", "desc"),
          startAfter(lastVisibleRef.current),
          limit(10) // Limit to 10 posts instead of 20
        );
      }

      const querySnapshot = await getDocs(postsQuery);
      const newPosts = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        likeCount: doc.data().likes?.length || 0, // Store the number of likes
        createdAt: doc.data().createdAt?.toDate() || "No Timestamp",
      }));

      if (querySnapshot.docs.length > 0) {
        lastVisibleRef.current = querySnapshot.docs[
          querySnapshot.docs.length - 1
        ]; // Update the last visible document

        // Combine existing posts and new posts
        const updatedPosts = [...posts, ...newPosts];

        // Sort the posts in decreasing order of likes
        const sortedPosts = updatedPosts.sort((a, b) => b.likeCount - a.likeCount);

        setPosts(sortedPosts); // Update the state with sorted posts

        // Save sorted posts to localStorage
        localStorage.setItem(CACHE_KEY, JSON.stringify(sortedPosts));
      } else {
        setHasMore(false); // No more posts available
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    }

    setLoading(false);
  };

  // Load cached posts on component mount
  useEffect(() => {
    const cachedPosts = localStorage.getItem(CACHE_KEY);
    if (cachedPosts) {
      setPosts(JSON.parse(cachedPosts)); // Use cached posts
    } else {
      fetchPosts(true); // Fetch posts if cache is empty
    }
  }, []); // Only run on mount

  // Handle infinite scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
          document.documentElement.offsetHeight - 100 &&
        hasMore &&
        !loading
      ) {
        fetchPosts(); // Fetch more posts when reaching the bottom
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loading]);

  return (
    <div className="post-list-container">
      <h2>All Posts</h2>
      {posts.length === 0 && loading ? (
        <p>Loading posts...</p>
      ) : posts.length === 0 ? (
        <p>No posts available.</p>
      ) : (
        <ul>
          {posts.map((post) => (
            <li key={post.id}>
              <Link
                to={`/post/${post.id}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div className="post-card">
                  <h3>{post.title}</h3>
                  <div
                    className="content"
                    dangerouslySetInnerHTML={{
                      __html: post.content?.substring(0, 100) || "No content available",
                    }}
                  ></div>
                  <div className="post-meta">
                    <img
                      src={post.photoURL || "/default-avatar.png"}
                      alt={post.displayName || "User"}
                      className="post-avatar"
                    />
                    <div>
                      <p>
                        <strong>{post.displayName || "Unknown User"}</strong>
                      </p>
                      <p>Likes: {post.likeCount}</p>
                      <p>
                        Posted:{" "}
                        {post.createdAt
                          ? formatDistanceToNow(post.createdAt, {
                              addSuffix: true,
                            })
                          : "Unknown time"}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {loading && posts.length > 0 && <p>Loading more posts...</p>}
    </div>
  );
};

export default PostList;
